import { Button, Card } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  Building2,
  Briefcase,
  CheckCircle2,
  Target,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import type { KPIOverviewSummary, KPIPeriodFilter } from "../../domain/types";
import type { KPIOverviewExplainArgs } from "../api/queries";
import { buildKpiOverviewExplainSearch } from "../utils/kpi-overview-explain-search";
import { KPICard } from "./KPICard";

interface KPISummaryCardsProps {
  data: KPIOverviewSummary;
  filter: KPIPeriodFilter;
}

export function KPISummaryCards({ data, filter }: KPISummaryCardsProps) {
  const nav = useNavigate();
  const { organization, departments, positions, employees } = data;

  const goToExplain = (args: KPIOverviewExplainArgs) => {
    nav({
      to: "/app/management/okr-kpi/explain",
      search: buildKpiOverviewExplainSearch(filter, args),
    });
  };

  return (
    <div className="space-y-6">
      {/* Organization Level Summary */}
      <div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-lg">KPI ອົງກອນ (ພາບລວມ)</h2>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              goToExplain({
                scope: "organization",
                includeSources: true,
                includeRecordSamples: true,
              })
            }
          >
            ລາຍລະອຽດຜົນ
          </Button>
        </div>

        {/* Organization Stats Cards */}
        <div
          className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-4"
          data-tourid="overview-summary-cards"
        >
          <Card className="border border-border bg-card p-4">
            <div className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-primary" />
              <div>
                <p className="text-muted-foreground text-xs">ທັງໝົດ</p>
                <p className="font-bold text-xl">{organization.total}</p>
              </div>
            </div>
          </Card>

          <Card className="border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/20">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <div>
                <p className="text-green-700 text-xs dark:text-green-400">
                  ຜ່ານ
                </p>
                <p className="font-bold text-green-700 text-xl dark:text-green-400">
                  {organization.pass}
                </p>
              </div>
            </div>
          </Card>

          <Card className="border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/20">
            <div className="flex items-center space-x-2">
              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div>
                <p className="text-red-700 text-xs dark:text-red-400">
                  ຕໍ່າກວ່າເປົ້າໝາຍ
                </p>
                <p className="font-bold text-red-700 text-xl dark:text-red-400">
                  {organization.fail}
                </p>
              </div>
            </div>
          </Card>

          <Card className="border border-border bg-card p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <div>
                <p className="text-muted-foreground text-xs">ອັດຕາຜ່ານ</p>
                <p className="font-bold text-xl">
                  {organization.passRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Organization KPI Cards */}
        {organization.kpis.length > 0 ? (
          <div
            className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
            data-tourid="overview-organization-kpis"
          >
            {organization.kpis.map((kpi) => (
              <KPICard
                key={kpi.id}
                kpi={kpi}
                onExplain={(k) =>
                  goToExplain({ scope: "organization", kpiId: k.id })
                }
              />
            ))}
          </div>
        ) : (
          <Card
            data-tourid="overview-organization-kpis"
            className="border border-border bg-card p-8 text-center"
          >
            <p className="text-muted-foreground">ບໍ່ມີຂໍ້ມູນ KPI ອົງກອນໃນໄລຍະນີ້</p>
          </Card>
        )}
      </div>

      {/* Department Level Summary */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h2 className="font-semibold text-lg">KPI ຕາມພະແນກ</h2>
        </div>

        {departments.length > 0 ? (
          <div className="space-y-6" data-tourid="overview-department-kpis">
            {departments.map((dept) => (
              <div key={dept.id}>
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="font-semibold">{dept.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <span className="text-green-600 dark:text-green-400">
                        ຜ່ານ: {dept.pass}
                      </span>
                      <span className="text-red-600 dark:text-red-400">
                        ຕໍ່າກວ່າເປົ້າໝາຍ: {dept.fail}
                      </span>
                      <span className="text-muted-foreground">
                        ອັດຕາຜ່ານ: {dept.passRate.toFixed(1)}%
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        goToExplain({
                          scope: "department",
                          scopeDepartmentId: dept.id,
                          includeSources: true,
                          includeRecordSamples: true,
                        })
                      }
                    >
                      ລາຍລະອຽດຜົນ
                    </Button>
                  </div>
                </div>
                {dept.kpis.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {dept.kpis.map((kpi) => (
                      <KPICard
                        key={kpi.id}
                        kpi={kpi}
                        onExplain={(k) =>
                          goToExplain({
                            scope: "department",
                            scopeDepartmentId: dept.id,
                            kpiId: k.id,
                          })
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <Card className="border border-border bg-card p-4 text-center">
                    <p className="text-muted-foreground text-sm">
                      ບໍ່ມີຂໍ້ມູນ KPI ສຳລັບພະແນກນີ້
                    </p>
                  </Card>
                )}
              </div>
            ))}
          </div>
        ) : (
          <Card
            className="border border-border bg-card p-8 text-center"
            data-tourid="overview-department-kpis"
          >
            <p className="text-muted-foreground">ບໍ່ມີຂໍ້ມູນ KPI ພະແນກໃນໄລຍະນີ້</p>
          </Card>
        )}
      </div>

      {/* Position Level Summary */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          <h2 className="font-semibold text-lg">KPI ຕາມຕຳແໜ່ງ</h2>
        </div>

        {positions.length > 0 ? (
          <div className="space-y-6" data-tourid="overview-position-kpis">
            {positions.map((pos) => (
              <div key={pos.id}>
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="font-semibold">{pos.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <span className="text-green-600 dark:text-green-400">
                        ຜ່ານ: {pos.pass}
                      </span>
                      <span className="text-red-600 dark:text-red-400">
                        ຕໍ່າກວ່າເປົ້າໝາຍ: {pos.fail}
                      </span>
                      <span className="text-muted-foreground">
                        ອັດຕາຜ່ານ: {pos.passRate.toFixed(1)}%
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        goToExplain({
                          scope: "position",
                          scopePositionId: pos.id,
                          includeSources: true,
                          includeRecordSamples: true,
                        })
                      }
                    >
                      ລາຍລະອຽດຜົນ
                    </Button>
                  </div>
                </div>
                {pos.kpis.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {pos.kpis.map((kpi) => (
                      <KPICard
                        key={kpi.id}
                        kpi={kpi}
                        onExplain={(k) =>
                          goToExplain({
                            scope: "position",
                            scopePositionId: pos.id,
                            kpiId: k.id,
                          })
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <Card className="border border-border bg-card p-4 text-center">
                    <p className="text-muted-foreground text-sm">
                      ບໍ່ມີຂໍ້ມູນ KPI ສຳລັບຕຳແໜ່ງນີ້
                    </p>
                  </Card>
                )}
              </div>
            ))}
          </div>
        ) : (
          <Card
            className="border border-border bg-card p-8 text-center"
            data-tourid="overview-position-kpis"
          >
            <p className="text-muted-foreground">ບໍ່ມີຂໍ້ມູນ KPI ຕຳແໜ່ງໃນໄລຍະນີ້</p>
          </Card>
        )}
      </div>

      {/* Employee Level Summary */}
      <div data-tourid="overview-employee-kpis">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-lg">KPI ພະນັກງານ (ເຊື່ອມສະເລ່ຍ)</h2>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              goToExplain({
                scope: "employee",
                includeSources: true,
                includeRecordSamples: true,
              })
            }
          >
            ລາຍລະອຽດຜົນ
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Card className="border border-border bg-card p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <div>
                <p className="text-muted-foreground text-xs">ຄ່າເຊື່ອມສະເລ່ຍ</p>
                <p className="font-bold text-xl">
                  {employees.average.toFixed(1)}%
                </p>
              </div>
            </div>
          </Card>

          <Card className="border border-border bg-card p-4">
            <div className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-primary" />
              <div>
                <p className="text-muted-foreground text-xs">ທັງໝົດ</p>
                <p className="font-bold text-xl">{employees.total}</p>
              </div>
            </div>
          </Card>

          <Card className="border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/20">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <div>
                <p className="text-green-700 text-xs dark:text-green-400">
                  ຜ່ານ
                </p>
                <p className="font-bold text-green-700 text-xl dark:text-green-400">
                  {employees.pass}
                </p>
              </div>
            </div>
          </Card>

          <Card className="border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/20">
            <div className="flex items-center space-x-2">
              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div>
                <p className="text-red-700 text-xs dark:text-red-400">
                  ຕໍ່າກວ່າເປົ້າໝາຍ
                </p>
                <p className="font-bold text-red-700 text-xl dark:text-red-400">
                  {employees.fail}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
